<script setup lang="ts">
/**
 * TaskTable Component
 * 
 * A reusable, feature-rich task table with inline editing capabilities.
 * 
 * Features:
 * - Inline editing: Click any cell to edit in place
 * - Keyboard shortcuts: Enter to save, Escape to cancel
 * - Conditional columns: Show/hide project and client columns based on context
 * - Priority color-coding: Visual indicators for task urgency
 * - Dropdown menus: Select status, assignee, priority, project, client
 * - Add new tasks: Inline add row with auto-focus
 * 
 * Design Patterns:
 * - Controlled component: All state is managed by parent via props and emits
 * - Flexible column layout: Columns adjust width based on visibility
 * - Consistent UX: Same editing experience for all field types
 * 
 * @example Usage in a page
 * <TaskTable
 *   :tasks="filteredTasks"
 *   :status-options="statusOptions"
 *   :assignee-options="assigneeOptions"
 *   :priority-options="priorityOptions"
 *   :show-project-column="true"
 *   :is-adding="isAddingTask"
 *   :new-task="newTask"
 *   @save-add="handleSaveTask"
 *   @delete-task="handleDeleteTask"
 * />
 */
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger } from '@/components/ui/context-menu';
import { DatePicker } from '@/components/ui/date-picker';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import StackedActionMenuPanel from '@/components/ui/stacked-action-menu/StackedActionMenuPanel.vue';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { Table, TableRow, TableBody, TableCell } from '@/components/ui/table';
import { ChevronRight, ChevronDown, AlertTriangle, MessageSquareText, MoreHorizontal, Plus } from 'lucide-vue-next';
import { buildClientSelectOptions, clientOwnershipLabel } from '~/lib/clientOwnership';
import TaskAssigneeDisplay from '@/components/tasks/TaskAssigneeDisplay.vue';
import TaskAssigneePicker from '@/components/tasks/TaskAssigneePicker.vue';
import TaskOptionMenu from '@/components/tasks/TaskOptionMenu.vue';
import TaskTableNewTaskRow from '@/components/tasks/TaskTableNewTaskRow.vue';
import TaskTableHeader from '@/components/tasks/TaskTableHeader.vue';
import TaskRowMetadata from '@/components/tasks/TaskRowMetadata.vue';
import TaskTimerAction from '@/components/time/TaskTimerAction.vue';
import EntityTagEditor from '@/components/entity/EntityTagEditor.vue';
import SelectionActionBar from '@/components/selection/SelectionActionBar.vue';
import type { SelectionAction, SelectionActionEvent } from '@/components/selection/types';
import type { Comment, ProjectStage, Task, TaskPriority, UploadedFile, TenantTaskStatusOption } from '~/types/models';
import { isPendingClientTaskWork } from '~/lib/taskClientResponse';
import { getTaskStatusOption } from '~/lib/taskConfig';
import { resolveTaskAssigneeIds, resolveTaskAssignees } from '~/lib/taskAssignees';
import { useTaskHierarchy, type DisplayTask } from '~/composables/tasks/useTaskHierarchy';

/**
 * Component Props
 * 
 * This component is designed as a controlled component - all state lives
 * in the parent and is passed down via props. Changes are communicated
 * back to the parent via emits.
 */
const props = withDefaults(defineProps<{
    /** Array of tasks to display */
    tasks: Task[];
    
    /** Available status values for dropdown */
    statusOptions: string[];

    /** Detailed status options with color metadata */
    statusOptionDetails?: TenantTaskStatusOption[];
    
    /** Available assignees for dropdown */
    assigneeOptions: Array<{ id: string; label: string; avatarURL?: string }>;
    
    /** Priority options with labels and colors */
    priorityOptions?: Array<{ value: TaskPriority; label: string; color?: string }>;
    
    /** Available projects for dropdown (optional) */
    projectOptions?: Array<{ id: string; name: string; clientId?: string; stages?: ProjectStage[] }>;

    /** Project milestones available to the inline task creation row */
    milestoneOptions?: ProjectStage[];
    
    /** Available clients for dropdown (optional) */
    clientOptions?: Array<{ id: string; name: string }>;
    
    /** Whether the add new task row is visible */
    isAdding: boolean;
    
    /** New task data being entered */
    newTask: { 
        title: string; 
        status: string; 
        assignee: string; 
        assigneeId?: string;
        assigneeIds?: string[];
        assignees?: Array<{ id: string; label: string; avatarURL?: string }>;
        priority: TaskPriority | '';
        dueDate: string;
        clientVisible: boolean;
        projectId?: string;
        milestoneId?: string;
        clientId?: string;
    };
    
    /** ID of task currently being edited (null if none) */
    editingId: string | null;
    
    /** Name of field currently being edited */
    editingField: string | null;
    
    /** Current value in the edit input */
    editingValue: string;
    
    /** Show/hide project column (useful for project-specific views) */
    showProjectColumn?: boolean;
    
    /** Show/hide client column */
    showClientColumn?: boolean;
    
    /** Selected task IDs for bulk operations */
    selectedTasks?: string[];
    
    /** Whether bulk operations are currently loading */
    bulkLoading?: boolean;
    
    /** Current sort field */
    sortField?: string | null;
    
    /** Current sort direction */
    sortDirection?: 'asc' | 'desc' | null;

    /** Render the table as read-only without inline editing */
    readOnly?: boolean;

    /** Show the bulk-selection checkbox column */
    showSelection?: boolean;

    /** Show metadata icons and prefetch related task files/comments */
    showTaskMetadata?: boolean;

    /** Load related metadata for rows; disable on API-backed portal lists. */
    prefetchTaskMetadata?: boolean;

    /** Whether task comment metadata should be fetched/rendered */
    showTaskCommentMetadata?: boolean;

    /** Surface client response state on task rows */
    showClientResponseState?: boolean;

    /** Label for the primary task action in read-only views */
    primaryActionLabel?: string;

    /** Alternate primary action label after a response already exists */
    primaryActionCompletedLabel?: string;

    /** Collapse parent tasks by default when no remembered state exists */
    defaultParentTasksCollapsed?: boolean;

    /** Optional localStorage key used to remember collapsed parent tasks in this view */
    parentTaskStateStorageKey?: string;

    /** Render inside a constrained dashboard surface */
    embedded?: boolean;

    /** Whether the table provides its own outer border and radius */
    contained?: boolean;

    /** Keep the header visible while the table body scrolls */
    stickyHeader?: boolean;

    /** Show a contextual hover action that starts the shared workspace timer */
    showTimeTracking?: boolean;

    /** When adding inline, render the new task row after this task instead of at the bottom */
    addAfterTaskId?: string;
}>(), {
    readOnly: false,
    showSelection: true,
    showTaskMetadata: true,
    prefetchTaskMetadata: true,
    showTaskCommentMetadata: true,
    showClientResponseState: false,
    defaultParentTasksCollapsed: false,
    parentTaskStateStorageKey: '',
    embedded: false,
    contained: true,
    stickyHeader: false,
    showTimeTracking: true,
    addAfterTaskId: '',
    milestoneOptions: () => [],
});

const emit = defineEmits<{
  (e: 'start-edit', id: string, field: string, value: string): void;
  (e: 'save-edit'): void;
  (e: 'save-assignees', taskId: string, assigneeIds: string[]): void;
  (e: 'cancel-edit'): void;
  (e: 'delete-task', id: string): void;
  (e: 'view-task', id: string): void;
  (e: 'start-add'): void;
  (e: 'insert-after-task', task: Task): void;
  (e: 'save-add'): void;
  (e: 'cancel-add'): void;
  (e: 'edit-value-change', value: string): void;
  (e: 'new-task-change', value: any): void;
  (e: 'edit-keydown', event: KeyboardEvent): void;
  (e: 'add-keydown', event: KeyboardEvent): void;
  (e: 'toggle-select', taskId: string): void;
  (e: 'toggle-select-all', taskIds?: string[]): void;
  (e: 'delete-selected'): void;
  (e: 'bulk-update', field: string, value: any): void;
  (e: 'sort', field: string): void;
  (e: 'create-subtask', parentTask: Task): void;
  (e: 'reparent-task', taskId: string, parentTaskId: string): void;
  (e: 'clear-parent', taskId: string): void;
  (e: 'task-primary-action', task: Task): void;
}>();

  const clientNameById = computed(() => Object.fromEntries(
    buildClientSelectOptions(props.clientOptions || []).map((client) => [client.value, client.label]),
  ));
  const {
    collapsedTaskIds,
    descendantTaskIdsMap,
    orderedTasks,
    toggleTaskCollapse,
  } = useTaskHierarchy({
    tasks: () => props.tasks,
    storageKey: () => props.parentTaskStateStorageKey,
    collapseParentsByDefault: () => props.defaultParentTasksCollapsed,
    showClientResponseState: () => props.showClientResponseState,
  });

/**
 * Check if a specific task field is being edited
 * 
 * @param id - Task ID
 * @param field - Field name
 * @returns True if this task/field combination is being edited
 */
const isEditing = (id: string, field: string) => {
    return props.editingId === id && props.editingField === field;
};

/**
 * Check if task is selected
 */
const isSelected = (taskId: string) => {
    return props.selectedTasks?.includes(taskId) ?? false;
};

/**
 * Check if all tasks are selected
 */
const filesStore = useFilesStore();
const commentsStore = useCommentsStore();
const entityTagsStore = useEntityTagsStore();
const taskTagSuggestions = computed(() => entityTagsStore.allTags);
const requestedTaskMetaIds = ref<Set<string>>(new Set());

async function saveTaskTags(taskId: string, tags: string[], colors: Record<string, string>) {
  await Promise.all([
    entityTagsStore.setEntityTags('task', taskId, tags),
    entityTagsStore.setTagColors(colors),
  ]);
}

watch(
  () => orderedTasks.value.map((task) => task.id),
  (taskIds) => {
    if (props.showTaskMetadata === false || props.prefetchTaskMetadata === false) return;

    const unseenTaskIds = taskIds.filter((taskId) => !requestedTaskMetaIds.value.has(taskId));
    if (!unseenTaskIds.length) return;

    const nextRequested = new Set(requestedTaskMetaIds.value);
    unseenTaskIds.forEach((taskId) => nextRequested.add(taskId));
    requestedTaskMetaIds.value = nextRequested;

    filesStore.fetchFilesForEntities('task', unseenTaskIds).catch(() => []);
    if (props.showTaskCommentMetadata !== false) {
      commentsStore.fetchCommentsForEntities('task', unseenTaskIds).catch(() => []);
    }
  },
  { immediate: true },
);

/**
 * Update all selected tasks with a new field value
 */
/**
 * Get project name from project ID
 * 
 * @param projectId - Project ID to look up
 * @returns Project name or fallback text
 */
const getProjectName = (projectId?: string) => {
    if (!projectId) return 'No Project';
    return props.projectOptions?.find(p => p.id === projectId)?.name || 'Unknown';
};

/**
 * Get client name from client ID
 * 
 * @param clientId - Client ID to look up
 * @returns Client name or fallback text
 */
const getClientName = (task: Task) => {
    return clientOwnershipLabel(task, {
        clientNameById: clientNameById.value,
    });
};

/**
 * Get priority display label and color
 * 
 * @param priority - Priority value
 * @returns Object with label and color class for badge styling
 */
const getPriorityDisplay = (priority?: TaskPriority) => {
    if (!priority) return { label: 'None', color: '#94a3b8' };
    const option = props.priorityOptions?.find(p => p.value === priority);
    return {
        label: option?.label || priority,
        color: option?.color || '#94a3b8'
    };
};

function getStatusBadgeStyle(status?: string) {
  const option = getTaskStatusOption(status, props.statusOptionDetails || []);
  const color = option?.color;
  if (!color) return undefined;

  return {
    borderColor: `color-mix(in srgb, ${color} 34%, transparent)`,
    backgroundColor: `color-mix(in srgb, ${color} 14%, transparent)`,
    color,
  };
}

function getAssigneeSnapshots(task: Task) {
  return resolveTaskAssignees(task, props.assigneeOptions);
}

function getAssigneeIds(task: Task) {
  return resolveTaskAssigneeIds(task);
}

function getStatusLabel(status?: string) {
  return getTaskStatusOption(status, props.statusOptionDetails || [])?.label || status || 'No status';
}

function saveEditingAssignees(taskId: string, ids: string[]) {
  emit('save-assignees', taskId, ids);
}

const statusOptionEntries = computed(() => (
  props.statusOptionDetails?.length
    ? props.statusOptionDetails
    : props.statusOptions.map(option => ({ value: option, label: option, color: '#64748b' }))
));
const projectMenuOptions = computed(() => (
  (props.projectOptions || []).map(project => ({ value: project.id, label: project.name }))
));
const newTaskMilestoneOptions = computed(() => {
  if (props.milestoneOptions?.length) return props.milestoneOptions;
  const projectId = String(props.newTask.projectId || '').trim();
  return props.projectOptions?.find(project => project.id === projectId)?.stages || [];
});
const clientMenuOptions = computed(() => (
  (props.clientOptions || []).map(client => ({ value: client.id, label: client.name }))
));

const taskSelectionActions = computed<SelectionAction[]>(() => [
  {
    id: 'status',
    label: 'Status',
    menuLabel: 'Set status',
    options: statusOptionEntries.value.map(status => ({ id: status.value, label: status.label, value: status.value })),
  },
  {
    id: 'priority',
    label: 'Priority',
    menuLabel: 'Set priority',
    options: [
      { id: 'none', label: 'No priority', value: '' },
      ...(props.priorityOptions || []).map(priority => ({ id: priority.value, label: priority.label, value: priority.value })),
    ],
  },
  {
    id: 'assigneeId',
    label: 'Assignee',
    menuLabel: 'Set assignee',
    options: [
      { id: 'unassigned', label: 'Unassigned', value: '' },
      ...props.assigneeOptions.map(assignee => ({ id: assignee.id, label: assignee.label, value: assignee.id })),
    ],
  },
  ...((props.projectOptions || []).length ? [{
    id: 'projectId',
    label: 'Project',
    menuLabel: 'Set project',
    options: [
      { id: 'none', label: 'No project', value: '' },
      ...(props.projectOptions || []).map(project => ({ id: project.id, label: project.name, value: project.id })),
    ],
  } satisfies SelectionAction] : []),
  ...((props.clientOptions || []).length ? [{
    id: 'clientId',
    label: 'Client',
    menuLabel: 'Set client',
    options: [
      { id: 'none', label: 'No client', value: '' },
      ...(props.clientOptions || []).map(client => ({ id: client.id, label: client.name, value: client.id })),
    ],
  } satisfies SelectionAction] : []),
  {
    id: 'clientVisible',
    label: 'Portal',
    menuLabel: 'Client portal',
    options: [
      { id: 'shared', label: 'Shared', value: true },
      { id: 'private', label: 'Private', value: false },
    ],
  },
  {
    id: 'delete',
    label: 'Delete',
    tone: 'destructive',
    separatorBefore: true,
  },
]);

function handleTaskSelectionAction(payload: SelectionActionEvent) {
  if (payload.actionId === 'delete') {
    emit('delete-selected');
    return;
  }
  if (!payload.option) return;
  emit('bulk-update', payload.actionId, payload.option.value ?? payload.option.id);
}

const tableColspan = computed(() => (
  5
    + (props.showSelection !== false ? 1 : 0)
    + (props.showClientColumn ? 1 : 0)
    + (props.showProjectColumn ? 1 : 0)
));

const taskColumnWidth = computed(() => {
  if (props.showClientColumn && props.showProjectColumn) return '32%';
  if (props.showClientColumn || props.showProjectColumn) return '42%';
  return '55%';
});

const statusColumnWidth = computed(() => {
  if (props.showClientColumn && props.showProjectColumn) return '10%';
  if (props.showClientColumn || props.showProjectColumn) return '11%';
  return '12%';
});

const priorityColumnWidth = computed(() => (
  props.showClientColumn || props.showProjectColumn ? '9%' : '10%'
));

const assigneeColumnWidth = computed(() => {
  if (props.showClientColumn && props.showProjectColumn) return '10%';
  if (props.showClientColumn || props.showProjectColumn) return '12%';
  return '13%';
});

const contextColumnWidth = computed(() => (
  props.showClientColumn && props.showProjectColumn ? '13%' : '13%'
));

const projectColumnWidth = computed(() => (
  props.showClientColumn && props.showProjectColumn ? '14%' : '13%'
));

const dueDateColumnWidth = computed(() => {
  if (props.showClientColumn && props.showProjectColumn) return '12%';
  if (props.showClientColumn || props.showProjectColumn) return '13%';
  return '10%';
});

function getTaskEntityKey(taskId: string) {
  return `task:${taskId}`;
}

function getTaskFiles(taskId: string): UploadedFile[] {
  return filesStore.filesByEntity[getTaskEntityKey(taskId)] ?? [];
}

function getTaskComments(taskId: string): Comment[] {
  return commentsStore.commentsByEntity[getTaskEntityKey(taskId)] ?? [];
}

function getParentTaskCandidates(task: Task): DisplayTask[] {
  const blockedTaskIds = new Set(descendantTaskIdsMap.value.get(task.id) ?? []);
  blockedTaskIds.add(task.id);

  return orderedTasks.value.filter(candidate => !blockedTaskIds.has(candidate.id));
}

const dropdownActionMenuTaskId = ref<string | null>(null);
const contextActionMenuTaskId = ref<string | null>(null);

function closeTaskActionMenus() {
  dropdownActionMenuTaskId.value = null;
  contextActionMenuTaskId.value = null;
}

function buildTaskActionMenuPages(task: Task) {
  const parentCandidates = getParentTaskCandidates(task);

  return [
    {
      id: 'root',
      title: 'Task',
      items: [
        { id: 'open-task', label: 'Open task', actionKey: 'open-task' },
        { id: 'edit-title', label: 'Edit title', actionKey: 'edit-title', disabled: props.readOnly },
        { id: 'add-subtask', label: 'Add subtask', actionKey: 'add-subtask', disabled: props.readOnly },
        {
          id: 'make-subtask',
          label: 'Make subtask of',
          pageId: 'parent-task',
          disabled: props.readOnly || parentCandidates.length === 0,
        },
        {
          id: 'clear-parent',
          label: 'Move to top level',
          actionKey: 'clear-parent',
          disabled: props.readOnly || !task.subtaskOfId,
        },
        { id: 'delete-task', label: 'Delete task', actionKey: 'delete-task', variant: 'destructive' as const, disabled: props.readOnly },
      ],
    },
    {
      id: 'parent-task',
      title: 'Choose parent task',
      emptyLabel: 'No eligible parent tasks.',
      items: parentCandidates.map((candidate) => ({
        id: `parent-${candidate.id}`,
        label: candidate.title,
        description: getStatusLabel(candidate.status),
        actionKey: 'reparent-task',
        value: candidate.id,
      })),
    },
  ];
}

function handleTaskActionMenuSelect(task: Task, item: { actionKey?: string; value?: string }) {
  switch (item.actionKey) {
    case 'open-task':
      emit('view-task', task.id);
      break;
    case 'edit-title':
      if (!props.readOnly) {
        startEditingTaskField(task.id, 'title', task.title);
      }
      break;
    case 'add-subtask':
      if (!props.readOnly) {
        emit('create-subtask', task);
      }
      break;
    case 'reparent-task':
      if (!props.readOnly && item.value) {
        emit('reparent-task', task.id, item.value);
      }
      break;
    case 'clear-parent':
      if (!props.readOnly) {
        emit('clear-parent', task.id);
      }
      break;
    case 'delete-task':
      if (!props.readOnly) {
        emit('delete-task', task.id);
      }
      break;
    default:
      break;
  }

  closeTaskActionMenus();
}

function startEditingTaskField(taskId: string, field: string, value: string) {
  if (props.readOnly) return;
  emit('start-edit', taskId, field, value);
}

function isAwaitingClientResponse(task: Task) {
  return isPendingClientTaskWork(task);
}

function clientResponseBadgeLabel(task: Task) {
  if (isAwaitingClientResponse(task)) return 'Action needed';
  if (String(task.clientResponseText || '').trim().length > 0) return 'Response added';
  return 'Shared task';
}

function clientPrimaryActionLabel(task: Task) {
  if (String(task.clientResponseText || '').trim().length > 0) {
    return props.primaryActionCompletedLabel || props.primaryActionLabel || 'Update response';
  }

  return props.primaryActionLabel || 'Respond';
}

function clientResponseHoverTitle(task: Task) {
  if (isAwaitingClientResponse(task)) return 'Action needed from you';
  if (String(task.clientResponseText || '').trim().length > 0) return 'Your latest response';
  return 'Client portal task';
}

function clientResponseHoverCopy(task: Task) {
  if (isAwaitingClientResponse(task)) {
    return String(task.clientResponsePrompt || '').trim() || 'The team is waiting on information, uploads, notes, or confirmation from you on this task.';
  }

  if (String(task.clientResponseText || '').trim().length > 0) {
    return String(task.clientResponseText || '').trim();
  }

  return 'This task is shared in the client portal. Open it if you need a closer look.';
}

const tableClass = computed(() => {
  if (props.showClientColumn && props.showProjectColumn) return 'w-full min-w-[1160px] table-fixed';
  if (props.showClientColumn || props.showProjectColumn) return 'w-full min-w-[1040px] table-fixed';
  return 'w-full min-w-[900px] table-fixed';
});

</script>

<template>
  <div
    :class="[
      'relative w-full overflow-x-auto bg-card',
      props.contained ? 'rounded-xl border border-border/70' : '',
      props.embedded ? 'task-table-shell' : '',
    ]"
  >
    <Table :class="tableClass">
      <colgroup>
        <col v-if="props.showSelection !== false" style="width: 2.75rem" />
        <col :style="{ width: taskColumnWidth }" />
        <col :style="{ width: statusColumnWidth }" />
        <col :style="{ width: priorityColumnWidth }" />
        <col :style="{ width: assigneeColumnWidth }" />
        <col v-if="showClientColumn" :style="{ width: contextColumnWidth }" />
        <col v-if="showProjectColumn" :style="{ width: projectColumnWidth }" />
        <col :style="{ width: dueDateColumnWidth }" />
      </colgroup>
      <TaskTableHeader
        :task-ids="orderedTasks.map(task => task.id)"
        :selected-task-ids="selectedTasks || []"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        :show-selection="props.showSelection !== false"
        :show-client-column="showClientColumn"
        :show-project-column="showProjectColumn"
        :read-only="props.readOnly"
        :sticky="props.stickyHeader"
        @toggle-all="taskIds => emit('toggle-select-all', taskIds)"
        @sort="field => emit('sort', field)"
      />

      <TableBody>
        <template
          v-for="taskItem in orderedTasks"
          :key="taskItem.id"
        >
        <ContextMenu
          :open="contextActionMenuTaskId === taskItem.id"
          @update:open="(open) => { contextActionMenuTaskId = open ? taskItem.id : null; }"
        >
          <ContextMenuTrigger as-child>
            <TableRow
              :class="[
                'group task-row-with-insert h-10',
                isSelected(taskItem.id) ? 'bg-muted/30' : '',
                props.showClientResponseState && isAwaitingClientResponse(taskItem)
                  ? 'bg-amber-50/50 dark:bg-amber-950/10'
                  : '',
              ]"
            >
              
              <!-- Checkbox -->
              <TableCell v-if="props.showSelection !== false" class="relative w-[2.75rem] min-w-[2.75rem] max-w-[2.75rem] overflow-visible">
                <button
                  v-if="!props.readOnly"
                  type="button"
                  class="task-row-insert-control task-row-insert-control--leading"
                  aria-label="Add task below this row"
                  @click.stop="emit('insert-after-task', taskItem)"
                >
                  <Plus class="size-3" />
                </button>
                <div
                  @click.stop="!props.readOnly && emit('toggle-select', taskItem.id)"
                  :class="[props.readOnly ? 'inline-flex opacity-60' : 'cursor-pointer inline-flex']"
                >
                  <Checkbox
                    :checked="isSelected(taskItem.id)"
                    :disabled="props.readOnly"
                  />
                </div>
              </TableCell>
              
              <!-- Task -->
            <TableCell class="relative overflow-visible transition-colors hover:bg-muted/40">
              <button
                v-if="!props.readOnly && props.showSelection === false"
                type="button"
                class="task-row-insert-control"
                aria-label="Add task below this row"
                @click.stop="emit('insert-after-task', taskItem)"
              >
                <Plus class="size-3" />
              </button>
              <input
                v-if="!props.readOnly && isEditing(taskItem.id, 'title')"
                :value="editingValue"
                @input="emit('edit-value-change', ($event.target as HTMLInputElement).value)"
                @blur="emit('save-edit')"
                @keydown="emit('edit-keydown', $event)"
                :class="`edit-input-${taskItem.id}`"
                class="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div
                v-else
                class="flex w-full items-center gap-2"
                :style="{ paddingLeft: `${taskItem._depth * 16}px` }"
              >
                <div
                  class="flex w-full min-w-0 items-center gap-2 rounded-md px-1.5 py-1.5 transition-colors hover:bg-background/80"
                  @click="emit('view-task', taskItem.id)"
                >
                  <div class="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <button
                      v-if="taskItem._hasChildren"
                      type="button"
                      class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-muted"
                      @click.stop="toggleTaskCollapse(taskItem.id)"
                    >
                      <ChevronRight v-if="collapsedTaskIds.has(taskItem.id)" class="h-3.5 w-3.5 text-muted-foreground" />
                      <ChevronDown v-else class="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                    <span v-else-if="taskItem._isSubtask" class="h-5 w-5 shrink-0"></span>
                    <span :class="['min-w-0 truncate', taskItem._isSubtask ? 'text-sm' : '']">{{ taskItem.title }}</span>
                    <EntityTagEditor
                      v-if="!props.readOnly"
                      :model-value="entityTagsStore.tagsFor('task', taskItem.id)"
                      :suggestions="taskTagSuggestions"
                      :colors="entityTagsStore.tagColors"
                      :save="(tags, colors) => saveTaskTags(taskItem.id, tags, colors)"
                      label="Task tags"
                      description="Add internal labels to organise and filter this task."
                      empty-label="Add tag"
                      placeholder="For example Admin or Client review"
                      :limit="2"
                      compact
                      trigger-class="min-h-6 px-1.5 py-0.5"
                    />
                    <Badge
                      v-if="props.showClientResponseState && taskItem.clientResponseRequired"
                      :class="[
                        'shrink-0',
                        isAwaitingClientResponse(taskItem)
                          ? 'border-amber-400/70 bg-amber-500/20 text-amber-800 hover:bg-amber-500/20 dark:text-amber-100'
                          : 'border-amber-300/60 bg-amber-500/10 text-amber-700 hover:bg-amber-500/10 dark:text-amber-200',
                      ]"
                    >
                      {{ clientResponseBadgeLabel(taskItem) }}
                    </Badge>
                    <span
                      v-if="taskItem._rollup"
                      class="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                      :title="`${taskItem._rollup.completionPercent}% complete (${taskItem._rollup.completed}/${taskItem._rollup.total} subtasks done)`"
                    >
                      {{ taskItem._rollup.completed }}/{{ taskItem._rollup.total }}
                      <AlertTriangle
                        v-if="taskItem._rollup.hasOverdue"
                        class="h-3 w-3 text-amber-500"
                        title="One or more subtasks are overdue"
                      />
                    </span>
                  </div>
                  <div class="ml-auto flex shrink-0 items-center justify-end gap-1 pr-1">
                    <TaskTimerAction
                      v-if="props.showTimeTracking && !props.readOnly"
                      :task="taskItem"
                      :project-options="projectOptions"
                      :client-options="clientOptions"
                    />
                    <HoverCard v-if="props.showClientResponseState && taskItem.clientResponseRequired">
                      <HoverCardTrigger as-child>
                        <button
                          type="button"
                          :class="[
                            'inline-flex h-6 w-6 items-center justify-center rounded-full opacity-80 transition-all group-hover:opacity-100',
                            isAwaitingClientResponse(taskItem)
                              ? 'text-amber-700 hover:bg-amber-100 hover:text-amber-800 dark:text-amber-200 dark:hover:bg-amber-950/40'
                              : 'text-sky-700/80 hover:bg-sky-50 hover:text-sky-800 dark:text-sky-200 dark:hover:bg-sky-950/40',
                          ]"
                          @click.stop="emit('view-task', taskItem.id)"
                        >
                          <AlertTriangle v-if="isAwaitingClientResponse(taskItem)" class="h-3.5 w-3.5" />
                          <MessageSquareText v-else class="h-3.5 w-3.5" />
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent class="w-72">
                        <div class="space-y-2">
                          <div>
                            <p class="text-sm font-medium">{{ clientResponseHoverTitle(taskItem) }}</p>
                            <p class="text-xs text-muted-foreground">
                              {{ isAwaitingClientResponse(taskItem) ? 'Open the task to respond, upload files, or add notes.' : 'You can reopen the task if you need to update this response.' }}
                            </p>
                          </div>
                          <p class="text-xs leading-5 text-foreground/80 whitespace-pre-wrap">
                            {{ clientResponseHoverCopy(taskItem) }}
                          </p>
                        </div>
                      </HoverCardContent>
                    </HoverCard>

                    <Button
                      v-if="props.showClientResponseState && taskItem.clientResponseRequired && props.primaryActionLabel"
                      size="sm"
                      variant="outline"
                      :class="[
                        'h-7 rounded-full px-3 text-xs',
                        isAwaitingClientResponse(taskItem)
                          ? 'border-amber-400/70 bg-amber-500/10 text-amber-800 hover:bg-amber-500/20 dark:text-amber-100'
                          : '',
                      ]"
                      @click.stop="emit('task-primary-action', taskItem)"
                    >
                      {{ clientPrimaryActionLabel(taskItem) }}
                    </Button>

                    <TaskRowMetadata
                      v-if="props.showTaskMetadata !== false"
                      :task="taskItem"
                      :files="getTaskFiles(taskItem.id)"
                      :comments="getTaskComments(taskItem.id)"
                      :show-comments="props.showTaskCommentMetadata !== false"
                      @open="emit('view-task', taskItem.id)"
                    />

                    <DropdownMenu
                      v-if="!props.readOnly"
                      :open="dropdownActionMenuTaskId === taskItem.id"
                      @update:open="(open) => { dropdownActionMenuTaskId = open ? taskItem.id : null; }"
                    >
                      <DropdownMenuTrigger as-child>
                        <button
                          type="button"
                          class="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground opacity-70 transition-all hover:bg-muted hover:text-foreground group-hover:opacity-100"
                          aria-label="Task actions"
                          @click.stop
                        >
                          <MoreHorizontal class="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" class="w-60">
                        <StackedActionMenuPanel
                          mode="dropdown"
                          :open="dropdownActionMenuTaskId === taskItem.id"
                          :pages="buildTaskActionMenuPages(taskItem)"
                          @select="handleTaskActionMenuSelect(taskItem, $event)"
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </TableCell>

        <!-- Status -->
        <TableCell
          :class="['transition-colors']"
        >
          <TaskOptionMenu
            v-if="!props.readOnly"
            :model-value="String(taskItem.status || '')"
            type="status"
            :options="statusOptionEntries"
            trigger-class="flex w-full items-center rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
            @update:model-value="(value) => { emit('start-edit', taskItem.id, 'status', value); emit('save-edit'); }"
          >
            <template #trigger>
              <Badge :style="getStatusBadgeStyle(taskItem.status)">{{ getStatusLabel(taskItem.status) }}</Badge>
            </template>
          </TaskOptionMenu>
          <Badge v-else :style="getStatusBadgeStyle(taskItem.status)">{{ getStatusLabel(taskItem.status) }}</Badge>
        </TableCell>

        <!-- Priority -->
        <TableCell
          :class="['transition-colors']"
        >
          <TaskOptionMenu
            v-if="!props.readOnly"
            :model-value="String(taskItem.priority || '')"
            type="priority"
            :options="priorityOptions"
            allow-clear
            clear-label="Clear priority"
            trigger-class="flex w-full items-center rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
            @update:model-value="(value) => { emit('start-edit', taskItem.id, 'priority', value); emit('save-edit'); }"
          >
            <template #trigger>
              <span class="text-sm font-medium" :style="{ color: getPriorityDisplay(taskItem.priority).color }">
                {{ getPriorityDisplay(taskItem.priority).label }}
              </span>
            </template>
          </TaskOptionMenu>
          <span v-else class="text-sm font-medium" :style="{ color: getPriorityDisplay(taskItem.priority).color }">
            {{ getPriorityDisplay(taskItem.priority).label }}
          </span>
        </TableCell>

        <!-- Assignee -->
        <TableCell
          :class="['transition-colors']"
        >
          <TaskAssigneePicker
            v-if="!props.readOnly"
            :model-value="getAssigneeIds(taskItem)"
            :options="assigneeOptions"
            minimal-trigger
            :show-chevron="false"
            :trigger-class="'h-9 w-full text-sm'"
            @update:model-value="(ids) => saveEditingAssignees(taskItem.id, ids)"
          />
          <TaskAssigneeDisplay v-else :assignees="getAssigneeSnapshots(taskItem)" compact :show-label="false" />
        </TableCell>

        <!-- Client -->
        <TableCell v-if="showClientColumn"
          :class="['transition-colors']"
        >
          <SearchableSelectMenu
            v-if="!props.readOnly"
            :model-value="String(taskItem.clientId || '')"
            :options="clientMenuOptions"
            placeholder="Client"
            search-placeholder="Search clients..."
            allow-clear
            clear-label="No Client"
            storage-key="task-table-client-picker"
            empty-state-label="Search to find a client."
            trigger-class="flex w-full items-center rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
            @update:model-value="(value) => { emit('start-edit', taskItem.id, 'clientId', value); emit('save-edit'); }"
          >
            <template #trigger="{ option }">
              <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                {{ option?.label || 'No Client' }}
              </span>
            </template>
          </SearchableSelectMenu>
          <span v-else class="text-sm">{{ getClientName(taskItem) }}</span>
        </TableCell>

        <!-- Project -->
        <TableCell v-if="showProjectColumn"
          :class="['min-w-0 overflow-hidden transition-colors']"
        >
          <SearchableSelectMenu
            v-if="!props.readOnly"
            :model-value="String(taskItem.projectId || '')"
            :options="projectMenuOptions"
            placeholder="Project"
            search-placeholder="Search projects..."
            allow-clear
            clear-label="No Project"
            storage-key="task-table-project-picker"
            empty-state-label="Search to find a project."
            trigger-class="flex w-full min-w-0 items-center overflow-hidden rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted/50"
            @update:model-value="(value) => { emit('start-edit', taskItem.id, 'projectId', value); emit('save-edit'); }"
          >
            <template #trigger="{ option }">
              <span class="min-w-0 truncate text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                {{ option?.label || 'No Project' }}
              </span>
            </template>
          </SearchableSelectMenu>
          <span v-else class="block truncate text-sm">{{ getProjectName(taskItem.projectId) }}</span>
        </TableCell>

        <!-- Due date -->
        <TableCell
          :class="['whitespace-nowrap transition-colors']"
        >
          <DatePicker
            v-if="!props.readOnly"
            :model-value="taskItem.dueDate || ''"
            minimal-trigger
            :show-icon="false"
            :class="'h-9 w-full text-sm'"
            placeholder="Select due date"
            @update:model-value="(value) => { emit('start-edit', taskItem.id, 'dueDate', value); emit('save-edit'); }"
          />
          <span v-else>
            {{
              taskItem.startDate && taskItem.endDate
                ? `${taskItem.startDate} to ${taskItem.endDate}`
                : (taskItem.startDate || taskItem.endDate || taskItem.dueDate)
            }}
          </span>
        </TableCell>
            </TableRow>
          </ContextMenuTrigger>
          <ContextMenuContent class="w-60">
            <StackedActionMenuPanel
              v-if="!props.readOnly"
              mode="context"
              :open="contextActionMenuTaskId === taskItem.id"
              :pages="buildTaskActionMenuPages(taskItem)"
              @select="handleTaskActionMenuSelect(taskItem, $event)"
            />
            <template v-else>
              <ContextMenuLabel>Task</ContextMenuLabel>
              <ContextMenuSeparator />
              <ContextMenuItem @select="emit('view-task', taskItem.id)">
                Open task
              </ContextMenuItem>
              <ContextMenuItem
                v-if="props.showClientResponseState && taskItem.clientResponseRequired && props.primaryActionLabel"
                @select="emit('task-primary-action', taskItem)"
              >
                {{ clientPrimaryActionLabel(taskItem) }}
              </ContextMenuItem>
            </template>
          </ContextMenuContent>
        </ContextMenu>

        <TaskTableNewTaskRow
          v-if="!props.readOnly && isAdding && props.addAfterTaskId === taskItem.id"
          :new-task="newTask"
          :status-options="statusOptionEntries"
          :priority-options="priorityOptions"
          :assignee-options="assigneeOptions"
          :client-options="clientMenuOptions"
          :project-options="projectMenuOptions"
          :milestone-options="newTaskMilestoneOptions"
          :show-selection="props.showSelection !== false"
          :show-client-column="showClientColumn"
          :show-project-column="showProjectColumn"
          @new-task-change="(value) => emit('new-task-change', value)"
          @add-keydown="(event) => emit('add-keydown', event)"
          @cancel-add="emit('cancel-add')"
        />

        </template>

      <!-- New task row -->
      <TaskTableNewTaskRow
        v-if="!props.readOnly && isAdding && !props.addAfterTaskId"
        :new-task="newTask"
        :status-options="statusOptionEntries"
        :priority-options="priorityOptions"
        :assignee-options="assigneeOptions"
        :client-options="clientMenuOptions"
        :project-options="projectMenuOptions"
        :milestone-options="newTaskMilestoneOptions"
        :show-selection="props.showSelection !== false"
        :show-client-column="showClientColumn"
        :show-project-column="showProjectColumn"
        @new-task-change="(value) => emit('new-task-change', value)"
        @add-keydown="(event) => emit('add-keydown', event)"
        @cancel-add="emit('cancel-add')"
      />

      <!-- Add new task trigger -->
      <TableRow v-if="!props.readOnly && !isAdding" @click="emit('start-add')" class="h-11 cursor-pointer hover:bg-muted/50">
        <TableCell :colspan="tableColspan" class="px-4 py-3 text-sm italic text-muted-foreground">
          Click to add a new task...
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>

  <SelectionActionBar
    :selected-count="selectedTasks?.length || 0"
    :loading="bulkLoading"
    :actions="taskSelectionActions"
    loading-label="Updating tasks…"
    @action="handleTaskSelectionAction"
    @clear="emit('toggle-select-all', orderedTasks.map(task => task.id))"
  />
  </div>
</template>

<style scoped>
.task-table-shell {
  height: 100%;
  min-height: 0;
}

.task-table-shell :deep([data-slot="table-container"]) {
  height: 100%;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.task-table-shell :deep([data-slot="table-container"]::-webkit-scrollbar) {
  display: none;
}

.task-table-shell :deep([data-slot="table"]) {
  min-height: 100%;
}

:deep([data-slot="table-head"]) {
  height: 2.5rem;
  padding-left: 0.875rem;
  padding-right: 0.875rem;
  color: var(--foreground);
}

:deep([data-slot="table-cell"]) {
  padding: 0.5rem 0.875rem;
}

:deep([data-slot="table-row"]) {
  border-color: color-mix(in srgb, var(--border) 72%, transparent);
}

.task-row-with-insert {
  transition: border-color 140ms ease, background-color 140ms ease;
}

.task-row-with-insert:hover {
  border-bottom-color: color-mix(in srgb, var(--foreground) 42%, var(--border));
}

.task-row-insert-control {
  position: absolute;
  left: -1.125rem;
  bottom: -0.625rem;
  z-index: 10;
  display: inline-flex;
  height: 1.25rem;
  width: 1.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid color-mix(in srgb, var(--primary) 55%, var(--border));
  background: var(--card);
  color: var(--primary);
  opacity: 0;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.08);
  transition: opacity 140ms ease, transform 140ms ease, background-color 140ms ease;
  transform: scale(0.92);
}

.task-row-insert-control--leading {
  left: 0.25rem;
}

.group:hover .task-row-insert-control,
.task-row-insert-control:focus-visible {
  opacity: 1;
  transform: scale(1);
}

.task-row-insert-control:hover {
  background: color-mix(in srgb, var(--primary) 8%, var(--card));
}
</style>
